import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

function Signup() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showVerification, setShowVerification] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifyingCode, setVerifyingCode] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate password strength
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (!/[A-Z]/.test(formData.password)) {
      setError('Password must contain at least one uppercase letter');
      return;
    }

    if (!/[a-z]/.test(formData.password)) {
      setError('Password must contain at least one lowercase letter');
      return;
    }

    if (!/\d/.test(formData.password)) {
      setError('Password must contain at least one number');
      return;
    }

    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password)) {
      setError('Password must contain at least one special character');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);

    try {
      const { confirmPassword, ...signupData } = formData;
      const response = await api.post('/api/auth/signup', signupData);
      
      // Show verification step
      setShowVerification(true);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Signup failed. Please try again.');
      console.error('Signup error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError('');

    if (verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setVerifyingCode(true);

    try {
      const response = await api.post('/api/auth/verify-email', {
        email: formData.email,
        code: verificationCode
      });

      // Login with the returned token
      if (response.data.token) {
        localStorage.setItem('svi_auth_token', response.data.token);
        localStorage.setItem('svi_user', JSON.stringify(response.data.user));
        navigate('/dashboard');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid verification code. Please try again.');
      console.error('Verification error:', err);
    } finally {
      setVerifyingCode(false);
    }
  };

  // If verification step, show verification form
  if (showVerification) {
    return (
      <div className="page">
        <header>
          <img className="logo" src="/logo.png" alt="Stewart Valuation Intelligence" />
          <h1>Remote Inspection</h1>
        </header>

        <main className="main">
          <div className="card" style={{maxWidth: '500px'}}>
            <div style={{textAlign: 'center', marginBottom: '24px'}}>
              <div style={{fontSize: '48px', marginBottom: '16px'}}>📧</div>
              <h2>Verify Your Email</h2>
            </div>

            <p style={{marginBottom: '16px', color: '#64748b'}}>
              We've sent a 6-digit verification code to:
            </p>
            <p style={{marginBottom: '24px', fontSize: '16px', fontWeight: '500', color: '#1f2937'}}>
              {formData.email}
            </p>

            <form onSubmit={handleVerifyCode}>
              <div className="section">
                <label htmlFor="verificationCode">Verification Code</label>
                <input
                  id="verificationCode"
                  type="text"
                  placeholder="Enter 6-digit code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required
                  autoFocus
                  maxLength={6}
                  style={{textAlign: 'center', fontSize: '18px', letterSpacing: '8px', fontFamily: 'monospace'}}
                />
                <p className="hint" style={{marginTop: '8px'}}>
                  Check your email for the code. It expires in 15 minutes.
                </p>
              </div>

              {error && (
                <div style={{
                  padding: '12px 16px',
                  marginBottom: '16px',
                  backgroundColor: '#fee2e2',
                  border: '1px solid #ef4444',
                  borderRadius: '8px',
                  color: '#991b1b'
                }}>
                  <strong>⚠️ Error:</strong> {error}
                </div>
              )}

              <div className="section">
                <button type="submit" disabled={verifyingCode || verificationCode.length !== 6}>
                  {verifyingCode ? 'Verifying...' : 'Verify & Create Account'}
                </button>
              </div>
            </form>

            <p className="hint" style={{textAlign: 'center', marginTop: '20px'}}>
              Didn't receive the code?{' '}
              <button
                type="button"
                onClick={() => {
                  setShowVerification(false);
                  setVerificationCode('');
                  setError('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#2563eb',
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  padding: 0,
                  font: 'inherit'
                }}
              >
                Go back and try again
              </button>
            </p>
          </div>
        </main>
      </div>
    );
  }

  // Otherwise, show signup form
  return (
    <div className="page">
      <header>
        <img className="logo" src="/logo.png" alt="Stewart Valuation Intelligence" />
        <h1>Remote Inspection</h1>
      </header>

      <main className="main">
        <div className="card" style={{maxWidth: '550px'}}>
          <h2>Registration</h2>
          <p style={{marginBottom: '24px', color: '#64748b'}}>
            Create an account to access the inspection console
          </p>

          <form onSubmit={handleSubmit}>
            <div className="row" style={{gap: '16px', marginBottom: '16px'}}>
              <div className="section" style={{flex: 1, marginBottom: 0}}>
                <label htmlFor="firstName">First Name</label>
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  placeholder="First name"
                  value={formData.firstName}
                  onChange={handleChange}
                  autoComplete="given-name"
                />
              </div>

              <div className="section" style={{flex: 1, marginBottom: 0}}>
                <label htmlFor="lastName">Last Name</label>
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  placeholder="Last name"
                  value={formData.lastName}
                  onChange={handleChange}
                  autoComplete="family-name"
                />
              </div>
            </div>

            <div className="section">
              <label htmlFor="email">Email *</label>
              <input
                id="email"
                name="email"
                type="email"
                placeholder="your.email@example.com"
                value={formData.email}
                onChange={handleChange}
                required
                autoComplete="email"
              />
            </div>

            <div className="section">
              <label htmlFor="password">Password *</label>
              <input
                id="password"
                name="password"
                type="password"
                placeholder="Enter password"
                value={formData.password}
                onChange={handleChange}
                required
                autoComplete="new-password"
              />
              <div style={{
                marginTop: '8px',
                padding: '12px',
                backgroundColor: '#f0f9ff',
                border: '1px solid #bae6fd',
                borderRadius: '6px',
                fontSize: '14px',
                color: '#0c4a6e'
              }}>
                <strong>Password Requirements:</strong>
                <ul style={{margin: '8px 0 0 20px', paddingLeft: '0'}}>
                  <li style={{marginBottom: '4px'}}>Minimum 8 characters</li>
                  <li style={{marginBottom: '4px'}}>At least one uppercase letter (A-Z)</li>
                  <li style={{marginBottom: '4px'}}>At least one lowercase letter (a-z)</li>
                  <li style={{marginBottom: '4px'}}>At least one number (0-9)</li>
                  <li style={{marginBottom: '4px'}}>At least one special character (!@#$%^&*etc.)</li>
                </ul>
              </div>
            </div>

            <div className="section">
              <label htmlFor="confirmPassword">Confirm Password *</label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                placeholder="Re-enter your password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                autoComplete="new-password"
              />
            </div>

            {error && (
              <div style={{
                padding: '12px 16px',
                marginBottom: '16px',
                backgroundColor: '#fee2e2',
                border: '1px solid #ef4444',
                borderRadius: '8px',
                color: '#991b1b'
              }}>
                <strong>⚠️ Error:</strong> {error}
              </div>
            )}

            <div className="section">
              <button type="submit" disabled={loading}>
                {loading ? 'Creating account...' : 'Create Account'}
              </button>
            </div>
          </form>

          <p className="hint" style={{textAlign: 'center', marginTop: '20px'}}>
            Already have an account? <Link to="/login" style={{color: '#2563eb', textDecoration: 'none', fontWeight: '500'}}>Sign in</Link>
          </p>

          <p className="hint" style={{textAlign: 'center', marginTop: '12px', fontSize: '14px', color: '#64748b'}}>
            By creating an account, you agree to our{' '}
            <Link to="/privacy-policy" style={{color: '#2563eb', textDecoration: 'none'}}>Privacy Policy</Link>
          </p>
        </div>
      </main>
    </div>
  );
}

export default Signup;

